import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useDocumentUpload } from '@/hooks/use-document-upload'
import { cn } from '@/lib/utils'
import { documentService } from '@/services/document.service'
import type { ChatMessage } from '@/stores/chat-store'
import type { MessageJobContext, MessageMatchData, MessageMetadata } from '@/types'
import {
  AlertTriangle,
  Briefcase,
  CheckCircle2,
  CircleHelp,
  FileText,
  Link,
  Loader,
  Send,
  X,
  XCircle,
} from 'lucide-react'
import { type ChangeEvent, type FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { isProtectedUrl, isUrl } from './chat-helpers'

export interface ChatInputProps {
  onSendMessage: (
    message: string,
    documentIds?: number[],
    matchData?: MessageMatchData,
  ) => Promise<void>
  onMatchComplete: (userMessage: ChatMessage, assistantMessage: ChatMessage) => void
  onDocumentReady?: (documentId: number) => void
  onSupportSubmitted?: (message: string) => void
  isSending: boolean
  supportMode?: boolean
  onSupportModeChange?: (active: boolean) => void
  userEmail?: string
  userName?: string
  initialResumeId?: number | undefined
  initialResumeName?: string | undefined
  initialMessage?: string | undefined
  compact?: boolean
}

export function ChatInput({
  onSendMessage,
  onMatchComplete,
  onDocumentReady,
  onSupportSubmitted,
  isSending,
  supportMode = false,
  onSupportModeChange,
  userEmail,
  userName,
  initialResumeId,
  initialResumeName,
  initialMessage,
  compact = false,
}: ChatInputProps) {
  const [isSupportSubmitting, setIsSupportSubmitting] = useState(false)
  const [supportError, setSupportError] = useState<string | null>(null)
  const [message, setMessage] = useState(initialMessage ?? '')
  const [showJobInput, setShowJobInput] = useState(false)
  const [jobInput, setJobInput] = useState('')
  const [detectedUrl, setDetectedUrl] = useState<string | null>(null)
  const [showPastePrompt, setShowPastePrompt] = useState(false)
  const [jobInputError, setJobInputError] = useState<string | null>(null)
  const [isAnalyzingMatch, setIsAnalyzingMatch] = useState(false)
  const [analysisStep, setAnalysisStep] = useState<'reading' | 'analyzing' | 'generating' | null>(
    null,
  )
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const initialResumeLoadedRef = useRef(false)
  const {
    document,
    isUploading,
    uploadProgress,
    error,
    uploadFile,
    clearDocument,
    setExistingDocument,
  } = useDocumentUpload()

  useEffect(() => {
    if (document?.status === 'ready' && onDocumentReady) {
      onDocumentReady(document.id)
    }
  }, [document?.status, document?.id, onDocumentReady])

  useEffect(() => {
    if (initialResumeId && initialResumeName && !initialResumeLoadedRef.current) {
      initialResumeLoadedRef.current = true
      setExistingDocument({
        id: initialResumeId,
        filename: initialResumeName,
        status: 'ready',
      })
      setShowJobInput(true)
    }
  }, [initialResumeId, initialResumeName, setExistingDocument])

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value)
    const textarea = e.target
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
  }

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      await uploadFile(file)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSend) return

    const messageToSend = message.trim()

    if (supportMode && onSupportSubmitted) {
      setIsSupportSubmitting(true)
      setSupportError(null)
      try {
        const token =
          typeof window !== 'undefined' && window.localStorage
            ? window.localStorage.getItem('auth_token')
            : null
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/v1/contact`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              name: userName || 'App User',
              email: userEmail || 'unknown',
              company: 'Individual',
              message: messageToSend,
            }),
          },
        )
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`)
        }
        setMessage('')
        if (textareaRef.current) textareaRef.current.style.height = 'auto'
        onSupportSubmitted(messageToSend)
        onSupportModeChange?.(false)
      } catch (error) {
        console.error('Failed to submit support request:', error)
        setSupportError('Failed to send. Please try again or email support@pelgo.com.')
      } finally {
        setIsSupportSubmitting(false)
      }
      return
    }

    const documentIds = document?.status === 'ready' ? [document.id] : undefined

    setMessage('')

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }

    await onSendMessage(messageToSend, documentIds)
  }

  const canSend = message.trim() && !isSending && !isUploading && !isSupportSubmitting

  const handleJobInputChange = (value: string) => {
    setJobInput(value)
    setJobInputError(null)

    if (isUrl(value)) {
      const url = value.trim()
      setDetectedUrl(url)
      if (isProtectedUrl(url)) {
        setShowPastePrompt(true)
      } else {
        setShowPastePrompt(false)
      }
    } else {
      setDetectedUrl(null)
      setShowPastePrompt(false)
    }
  }

  const handleAnalyzeMatch = useCallback(async () => {
    if (!document || document.status !== 'ready') return
    if (!jobInput.trim()) return

    setIsAnalyzingMatch(true)
    setAnalysisStep('reading')
    setJobInputError(null)

    try {
      const useUrl = detectedUrl && !showPastePrompt

      if (detectedUrl && showPastePrompt && jobInput.trim() === detectedUrl) {
        setJobInputError('Please paste the job description text instead of the URL.')
        setIsAnalyzingMatch(false)
        setAnalysisStep(null)
        return
      }

      const jobPosting = await documentService.createJobPosting(
        useUrl ? { url: detectedUrl } : { text: jobInput.trim() },
      )

      setAnalysisStep('analyzing')
      const matchResult = await documentService.analyzeMatch(document.id, {
        job_posting_id: jobPosting.id,
      })

      setAnalysisStep('generating')

      const isInvalidResult =
        matchResult.overall_score === 0 &&
        (!matchResult.matched_skills || matchResult.matched_skills.length === 0) &&
        (!matchResult.missing_skills || matchResult.missing_skills.length === 0)

      if (isInvalidResult) {
        setShowPastePrompt(true)
        setJobInput('')
        setDetectedUrl(null)
        setJobInputError(
          "Couldn't analyze this job posting. The page may require login or have restricted access. Please paste the job description text directly.",
        )
        return
      }

      setShowJobInput(false)
      setJobInput('')
      setDetectedUrl(null)
      setShowPastePrompt(false)

      const matchData: MessageMatchData = {
        score: matchResult.overall_score,
        breakdown: matchResult.breakdown,
        matchedSkills: matchResult.matched_skills,
        missingSkills: matchResult.missing_skills,
        recommendations: matchResult.recommendations,
        ...(matchResult.explanation && { explanation: matchResult.explanation }),
        ...(jobPosting.title && { jobTitle: jobPosting.title }),
        ...(jobPosting.company && { company: jobPosting.company }),
      }

      const fullJobText = jobPosting.raw_text
      const jobContext: MessageJobContext = {
        ...(jobPosting.title && { title: jobPosting.title }),
        ...(jobPosting.company && { company: jobPosting.company }),
        snippet: fullJobText.substring(0, 100) + (fullJobText.length > 100 ? '...' : ''),
        fullText: fullJobText,
        isUrl: !!useUrl,
      }

      const userMetadata: MessageMetadata = {
        match_data: matchData,
        job_context: jobContext,
      }

      const userMessage: ChatMessage = {
        id: `match-user-${Date.now()}`,
        content: 'Compare my resume to this job posting',
        role: 'user',
        created_at: new Date().toISOString(),
        msg_metadata: userMetadata,
        matchData,
        jobContext,
      }

      const assistantMessage: ChatMessage = {
        id: `match-assistant-${Date.now()}`,
        content: matchResult.explanation || matchResult.summary || 'Match analysis complete.',
        role: 'assistant',
        created_at: new Date().toISOString(),
      }

      onMatchComplete(userMessage, assistantMessage)
    } catch (err: unknown) {
      console.error('Failed to analyze match:', err)

      if (
        detectedUrl &&
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof err.message === 'string' &&
        (err.message.includes('403') ||
          err.message.includes('401') ||
          err.message.includes('fetch'))
      ) {
        setShowPastePrompt(true)
        setJobInput('')
        setDetectedUrl(null)
        setJobInputError('This URL requires login. Please paste the job description text instead.')
      } else {
        setJobInputError('Failed to analyze job match. Please check your input and try again.')
      }
    } finally {
      setIsAnalyzingMatch(false)
      setAnalysisStep(null)
    }
  }, [document, jobInput, detectedUrl, showPastePrompt, onMatchComplete])

  return (
    <div
      className={cn(
        'border-t bg-gradient-to-r from-blue-50/30 to-blue-50/30 dark:from-blue-950/10 dark:to-blue-950/10',
        compact ? 'p-3' : 'p-4',
      )}
    >
      {error && <div className="mb-3 text-xs text-red-600">{error}</div>}
      {supportError && <div className="mb-3 text-xs text-red-600">{supportError}</div>}

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".pdf,application/pdf"
        onChange={handleFileSelect}
      />

      <div
        className={cn(
          'rounded-2xl border shadow-sm transition-all',
          supportMode
            ? 'bg-blue-50 dark:bg-blue-950/30 border-blue-400 ring-2 ring-blue-400/30'
            : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent',
        )}
      >
        {!supportMode && (document || showJobInput) && (
          <div className="px-3 pt-3 pb-2 flex flex-wrap items-center gap-2">
            {document && (
              <div
                className={cn(
                  'flex flex-col rounded-lg text-xs border overflow-hidden',
                  document.status === 'ready'
                    ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800'
                    : document.status === 'invalid_document'
                      ? 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800'
                      : document.status === 'failed'
                        ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
                        : 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800',
                )}
              >
                <div className="flex items-center gap-2 px-3 py-1.5">
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate max-w-[120px] font-medium">{document.filename}</span>
                  {document.status === 'uploading' && (
                    <span className="text-blue-600 font-medium ml-1">
                      {uploadProgress > 0 ? `${uploadProgress}%` : 'Starting...'}
                    </span>
                  )}
                  {document.status === 'processing' && (
                    <>
                      <Loader className="h-3 w-3 animate-spin text-blue-600" />
                      <span className="text-blue-600">Processing...</span>
                    </>
                  )}
                  {document.status === 'ready' && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                  )}
                  {document.status === 'invalid_document' && (
                    <AlertTriangle className="h-3.5 w-3.5 text-yellow-600" />
                  )}
                  {document.status === 'failed' && <XCircle className="h-3.5 w-3.5 text-red-600" />}
                  <button
                    type="button"
                    onClick={clearDocument}
                    className="p-0.5 hover:bg-black/10 rounded transition-colors ml-auto"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                {document.status === 'uploading' && (
                  <div className="h-1 bg-blue-100 dark:bg-blue-900/50">
                    <div
                      className="h-full bg-blue-500 transition-all duration-300 ease-out"
                      style={{ width: `${Math.max(uploadProgress, 0)}%` }}
                    />
                  </div>
                )}
                {document.status === 'processing' && (
                  <div className="h-1 bg-blue-100 dark:bg-blue-900/50 overflow-hidden">
                    <div className="h-full w-1/3 bg-blue-500 animate-[indeterminate_1.5s_ease-in-out_infinite]" />
                  </div>
                )}
                {document.status === 'invalid_document' && (
                  <div className="px-3 py-1.5 text-xs text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 border-t border-yellow-200 dark:border-yellow-800">
                    This is not a valid resume. Please upload a valid one and try again.
                  </div>
                )}
                {document.status === 'failed' && (
                  <div className="px-3 py-1.5 text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border-t border-red-200 dark:border-red-800">
                    {document.error || 'Processing failed. Please try again.'}
                  </div>
                )}
              </div>
            )}

            {document && document.status === 'ready' && !showJobInput && (
              <button
                type="button"
                onClick={() => setShowJobInput(true)}
                disabled={isSending || isAnalyzingMatch}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                <Briefcase className="h-3.5 w-3.5" />
                <span>Add job</span>
              </button>
            )}

            {showJobInput && (
              <div className="w-full mt-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {showPastePrompt ? 'Paste Job Description' : 'Add Job Posting'}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowJobInput(false)
                      setJobInput('')
                      setDetectedUrl(null)
                      setShowPastePrompt(false)
                      setJobInputError(null)
                      setIsAnalyzingMatch(false)
                      setAnalysisStep(null)
                    }}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                {showPastePrompt && (
                  <div className="mb-2 p-2 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      This site requires login - paste text instead
                    </p>
                  </div>
                )}

                {detectedUrl && !showPastePrompt && (
                  <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-500">
                    <Link className="h-3 w-3" />
                    <span className="truncate">{detectedUrl}</span>
                  </div>
                )}

                <Textarea
                  value={jobInput}
                  onChange={(e) => handleJobInputChange(e.target.value)}
                  placeholder={
                    showPastePrompt
                      ? 'Paste the job description text here...'
                      : 'Paste job URL or description...'
                  }
                  className="min-h-[80px] text-base md:text-xs border-gray-200 dark:border-gray-600 focus-visible:ring-blue-500"
                  disabled={isAnalyzingMatch}
                />

                {jobInputError && (
                  <div className="mt-2 p-2 rounded bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                    <p className="text-xs text-red-700 dark:text-red-400">{jobInputError}</p>
                  </div>
                )}

                {isAnalyzingMatch && (
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2 text-xs">
                      {analysisStep === 'reading' ? (
                        <Loader className="h-3 w-3 animate-spin text-blue-600" />
                      ) : (
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                      )}
                      <span
                        className={analysisStep === 'reading' ? 'text-blue-600' : 'text-gray-500'}
                      >
                        {analysisStep === 'reading' ? 'Reading job...' : 'Job read'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {analysisStep === 'analyzing' ? (
                        <Loader className="h-3 w-3 animate-spin text-blue-600" />
                      ) : analysisStep === 'generating' ? (
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                      ) : (
                        <div className="h-3 w-3 rounded-full border border-gray-300" />
                      )}
                      <span
                        className={analysisStep === 'analyzing' ? 'text-blue-600' : 'text-gray-400'}
                      >
                        {analysisStep === 'analyzing' ? 'Analyzing...' : 'Analyze match'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {analysisStep === 'generating' ? (
                        <Loader className="h-3 w-3 animate-spin text-blue-600" />
                      ) : (
                        <div className="h-3 w-3 rounded-full border border-gray-300" />
                      )}
                      <span
                        className={
                          analysisStep === 'generating' ? 'text-blue-600' : 'text-gray-400'
                        }
                      >
                        {analysisStep === 'generating' ? 'Generating...' : 'Generate tips'}
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-2 flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAnalyzeMatch}
                    disabled={
                      isAnalyzingMatch ||
                      document?.status !== 'ready' ||
                      !jobInput.trim() ||
                      (showPastePrompt && !!detectedUrl && jobInput.trim() === detectedUrl)
                    }
                    className="text-xs h-7 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700"
                  >
                    {isAnalyzingMatch ? (
                      <>
                        <Loader className="h-3 w-3 animate-spin mr-1" />
                        Analyzing
                      </>
                    ) : document?.status !== 'ready' ? (
                      'Waiting...'
                    ) : (
                      'Compare'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex items-end gap-2 px-3 py-2">
          {supportMode ? (
            <button
              type="button"
              className="shrink-0 p-1.5 mb-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              onClick={() => {
                onSupportModeChange?.(false)
                setSupportError(null)
              }}
              title="Cancel support request"
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          ) : (
            <>
              {/* Resume upload from chat disabled — use /resume instead
              <button
                type="button"
                className="shrink-0 p-1.5 mb-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={isSending || isUploading || !!document}
                onClick={() => fileInputRef.current?.click()}
                title="Upload resume (PDF)"
              >
                <Paperclip className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
              */}
              <button
                type="button"
                className="shrink-0 p-1.5 mb-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                onClick={() => onSupportModeChange?.(true)}
                title="Contact support"
              >
                <CircleHelp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </>
          )}

          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && supportMode) {
                onSupportModeChange?.(false)
                setSupportError(null)
              } else if (e.key === 'Enter' && !e.shiftKey && canSend) {
                e.preventDefault()
                handleSubmit(e as unknown as FormEvent)
              }
            }}
            placeholder={
              supportMode
                ? 'Message a Pelgo team member...'
                : document?.status === 'uploading' || document?.status === 'processing'
                  ? 'Processing resume...'
                  : '+ Ask me anything'
            }
            disabled={isSending || isSupportSubmitting}
            rows={1}
            className="flex-1 bg-transparent border-none outline-none text-base md:text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 resize-none py-1.5 max-h-[200px]"
          />

          <button
            type="submit"
            disabled={!canSend}
            className={cn(
              'shrink-0 p-2 mb-0.5 rounded-full transition-all',
              canSend
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed',
            )}
          >
            {isSending || isSupportSubmitting ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </form>
      </div>

      {!compact && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Pelgo can make mistakes. Check important info.
        </p>
      )}
    </div>
  )
}
